import SubscribeForm from "../components/SubscribeForm";

// Rainbow accents pulled from the logo letters
const RAINBOW = ["#EE6A96", "#F5A05F", "#EDB945", "#7FBC7B", "#6FB3E0", "#A98BD4"];
const CREED = ["Rescue", "Foster", "Sponsor", "Comfort", "Love"];

function Eyebrow({ children }) {
  return <p className="eyebrow">{children}</p>;
}

export default function Landing() {
  return (
    <>
      {/* ---------- Sticky header ---------- */}
      <header className="site-header">
        <div className="site-header__inner">
          <a className="brand" href="#top" aria-label="TiCoMi Foundation, back to top">
            <img className="brand__mark" src="/ticomi-logo.webp" alt="" width="38" height="38" />
            <span className="brand__name">TiCoMi Foundation</span>
          </a>
          <nav className="site-nav" aria-label="Primary">
            <a href="#about">About</a>
            <a href="#mission">Mission</a>
            <a href="#history">Our History</a>
            <a href="#why-donate">Why Donate</a>
            <a href="#support">Support</a>
          </nav>
        </div>
      </header>

      <main id="top">
        {/* ---------- 1. Hero ---------- */}
        <section className="section hero" aria-label="Introduction">
          <div className="wrap hero__inner">
            <picture>
              <source srcSet="/ticomi-logo.webp" type="image/webp" />
              <img
                className="hero__logo"
                src="/ticomi-logo-880.png"
                width="340"
                height="340"
                alt={"TiCoMi Foundation \u2014 Different Hearts, One Love"}
                fetchPriority="high"
              />
            </picture>
            <h1 className="hero__title">Age should never determine a dog's worth.</h1>
            <p className="hero__tag">Different Hearts, One Love</p>
            <p className="hero__lede">
              A 501(c)(3) nonprofit rescuing abandoned and vulnerable senior dogs
              {" \u2014 "}with a special love for senior Chihuahuas and French Bulldogs.
            </p>
          </div>
        </section>

        {/* ---------- 2. Creed band ---------- */}
        <section className="creed" aria-label="Our creed">
          <div className="wrap">
            <ul className="creed__list">
              {CREED.map((word, i) => (
                <li key={word} style={{ color: RAINBOW[i] }}>
                  {word}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ---------- 3. About Us ---------- */}
        <section className="section" id="about">
          <div className="wrap">
            <Eyebrow>About Us</Eyebrow>
            <h2>The ones who get left behind</h2>
            <p>
              TiCoMi Foundation is a 501(c)(3) nonprofit organization dedicated to helping
              abandoned and vulnerable senior dogs, with a special focus on senior Chihuahuas
              and French Bulldogs.
            </p>
            <p>
              Many senior dogs are left behind when their owners pass away, become ill, enter
              long-term care, or can no longer care for them. These precious dogs can face
              loneliness, medical needs, shelter overcrowding, and an uncertain future.
            </p>
            <p>
              We strive to give every senior dog safety, dignity, compassionate care, and a
              second chance at a loving home.
            </p>
          </div>
        </section>

        {/* ---------- 4. Our Mission ---------- */}
        <section className="section section--panel" id="mission">
          <div className="wrap">
            <Eyebrow>Our Mission</Eyebrow>
            <h2>A safe path forward</h2>
            <p>
              Our mission is to rescue and support abandoned senior Chihuahuas and French
              Bulldogs, providing them with the care, protection, and compassion they deserve.
            </p>
            <p>
              Through rescue, foster care, sponsorship, veterinary support, transportation, and
              adoption, we work to create a safe path forward for senior dogs in need.
            </p>
            <p>
              TiCoMi Foundation serves as a liaison and bridge{" \u2014 "}connecting senior dogs
              with shelters, caregivers, foster families, veterinary professionals, sponsors,
              adopters, volunteers, and compassionate members of our community.
            </p>
            <p className="pullquote">We need many helping hands to fulfill our duty.</p>
          </div>
        </section>

        {/* ---------- 5. Rainbow Bridge ---------- */}
        <section className="section section--white bridge" aria-label="TiCoMi Angels">
          <div className="wrap bridge__inner">
            <div className="bridge__arc" aria-hidden="true">
              {RAINBOW.map((c) => (
                <span key={c} style={{ background: c }} />
              ))}
            </div>
            <Eyebrow>TiCoMi Angels</Eyebrow>
            <h2>Nothing to fear. Only love.</h2>
            <p>
              For dogs who cannot be saved, or who have reached the end of their natural journey,
              we believe they deserve to transition peacefully to the Rainbow Bridge{" \u2014 "}
              surrounded by comfort, dignity, and love.
            </p>
            <p>We envision our TiCoMi Angels waiting to welcome and greet them.</p>

            <div className="bridge__lines">
              <p>A place without fear.</p>
              <p>A place without pain.</p>
              <p>A place where they are completely safe, protected, and surrounded by Love.</p>
            </div>
          </div>
        </section>

        {/* ---------- 6. Our History ---------- */}
        <section className="section" id="history">
          <div className="wrap">
            <Eyebrow>Our History</Eyebrow>
            <h2>It started with three dogs</h2>
            <p>
              TiCoMi Foundation was inspired by a lifelong love of dogs and the special bond
              shared with three beloved companions.
            </p>

            <div className="dogcards">
              {[
                { a: "Ti", b: "To" },
                { a: "Co", b: "Ci" },
                { a: "Mi", b: "a" },
              ].map((d) => (
                <div className="dogcard" key={d.a}>
                  <span className="dogcard__name">
                    <span className="dogcard__lead">{d.a}</span>
                    {d.b}
                  </span>
                  <span className="dogcard__label">Beloved</span>
                </div>
              ))}
            </div>

            <div className="became">
              <span className="became__small">Which became</span>
              <span className="became__word">TiCoMi</span>
            </div>

            <p className="story-note">
              TiTo and CoCi, brother and sister, were both rescued at the age of six. They went
              on to live wonderful, loving lives as cherished King and princess of the TiCoMi
              world. Their story became part of the foundation of TiCoMi's commitment to giving
              overlooked dogs the chance to experience safety, love, dignity, and a true family
              home.
            </p>

            <figure className="collage">
              <div className="collage__grid">
                <img
                  className="collage__img collage__img--tall"
                  src="/history/dog3.webp"
                  alt="TiTo, the little King, wearing a red royal crown"
                  loading="lazy"
                />
                <img
                  className="collage__img"
                  src="/history/dogjersey.webp"
                  alt="A senior Chihuahua resting in a cozy red and blue jersey"
                  loading="lazy"
                />
                <img
                  className="collage__img"
                  src="/history/dog2.webp"
                  alt="Three Chihuahuas curled up together at home"
                  loading="lazy"
                />
                <img
                  className="collage__img"
                  src="/history/dog5.webp"
                  alt="A dapper Chihuahua in a beanie and doggles, ready for an adventure"
                  loading="lazy"
                />
                <img
                  className="collage__img collage__img--wide"
                  src="/history/dog4v2.webp"
                  alt="The TiCoMi family together, dressed up and full of love"
                  loading="lazy"
                />
              </div>
              <figcaption className="collage__caption">
                The dogs who started it all &mdash; loved every day of their lives.
              </figcaption>
            </figure>

            <p>
              Their lives became the inspiration behind the name and the heart of our mission.
              What began with the love of three special dogs has grown into a vision to help
              senior dogs who are often forgotten, abandoned, or left behind.
            </p>
            <p>
              Our journey is dedicated to making sure these dogs are never alone when they need
              love the most.
            </p>
            <p className="pullquote">
              Every life matters. Every senior deserves dignity. Every dog deserves to know love
              until the very end.
            </p>
          </div>
        </section>

        {/* ---------- 7. Why Donate ---------- */}
        <section className="section why" id="why-donate">
          <div className="wrap">
            <Eyebrow>Why Donate</Eyebrow>
            <h2>They ran out of time. You can give them more.</h2>
            <p>
              Tonight, somewhere in a loud and crowded shelter, a grey-muzzled dog is waiting{" \u2014 "}
              confused about why the family they loved for a lifetime never came back. Senior dogs
              are the first to be overlooked and the last to be chosen. For them, time is the one
              thing they don't have.
            </p>
            <p>
              A donation to TiCoMi is not a transaction. It is the moment a frightened old dog
              stops being invisible. It is the difference between a cold concrete run and a warm
              lap, between being forgotten and being someone's whole heart again.
            </p>

            <ul className="why-reasons">
              <li className="why-reasons__item">
                <span className="why-reasons__bead" aria-hidden="true" style={{ background: RAINBOW[0] }} />
                <div>
                  <span className="why-reasons__title">You cover the care they can't wait for</span>
                  <p>
                    Seniors arrive with real, urgent needs{" \u2014 "}dentals, bloodwork, medication,
                    and gentle comfort care that shelters simply cannot afford. Your gift pays for it.
                  </p>
                </div>
              </li>
              <li className="why-reasons__item">
                <span className="why-reasons__bead" aria-hidden="true" style={{ background: RAINBOW[3] }} />
                <div>
                  <span className="why-reasons__title">You get them out, fast</span>
                  <p>
                    Every extra day in a kennel is harder on an old heart. You move them into a quiet
                    foster home where they can finally rest, safe and unafraid.
                  </p>
                </div>
              </li>
              <li className="why-reasons__item">
                <span className="why-reasons__bead" aria-hidden="true" style={{ background: RAINBOW[5] }} />
                <div>
                  <span className="why-reasons__title">You give them the ending they deserve</span>
                  <p>
                    Whether their next chapter is months or years, you make sure it is spent loved,
                    warm, and treated with dignity{" \u2014 "}never alone.
                  </p>
                </div>
              </li>
            </ul>

            <p className="pullquote">No dog should have to spend their last chapter alone.</p>

            {/* TODO: replace #signup with the real payment processor once ready. Points to the email sign-up for now. */}
            <div className="why__cta">
              <a className="btn btn--solid" href="#signup">
                Donate now
              </a>
              <p className="why__cta-note">Every gift, of any size, changes a senior dog's story.</p>
            </div>
          </div>
        </section>

        {/* ---------- 8. Support ---------- */}
        <section className="section section--panel" id="support">
          <div className="wrap">
            <Eyebrow>Every Purchase. Every Donation. Every Life.</Eyebrow>
            <h2>You become part of their journey</h2>
            <p>
              Every item sold and every donation received helps TiCoMi Foundation serve precious
              senior dogs who are often overlooked.
            </p>

            <ul className="support">
              {[
                {
                  t: "Care that can't wait",
                  d: "Your support helps provide food, veterinary care, medications, and the treatments seniors so often need.",
                },
                {
                  t: "Getting them there",
                  d: "Transportation and foster care move dogs out of shelters and into safety.",
                },
                {
                  t: "The road home",
                  d: "Every dollar helps us work toward saving overlooked lives and helping these paw friends make it home.",
                },
              ].map((s, i) => (
                <li className="support__card" key={s.t}>
                  <span
                    className="support__bead"
                    aria-hidden="true"
                    style={{ background: RAINBOW[i] }}
                  />
                  <div>
                    <span className="support__title">{s.t}</span>
                    <p className="support__desc">{s.d}</p>
                  </div>
                </li>
              ))}
            </ul>

            <p>
              When you purchase an item or make a donation, you are not simply supporting an
              organization. You are becoming part of their journey{" \u2014 "}one of the helping
              hands giving an overlooked senior dog a chance to be seen, loved, protected, and
              cared for.
            </p>
            <p className="pullquote">Every dollar. Every paw. Every second chance.</p>
          </div>
        </section>

        {/* ---------- 8. Call to action ---------- */}
        <section className="section cta" aria-label="Get involved">
          <div className="wrap cta__inner">
            <h2>Be part of this beautiful journey</h2>
            <p className="cta__creed">Rescue • Foster • Sponsor • Comfort • Love</p>

            {/* TODO: replace #signup with the real payment processor / volunteer form once ready. Points to the email sign-up for now. */}
            <div className="cta__buttons">
              <a className="btn btn--solid" href="#signup">
                Donate
              </a>
              <a className="btn btn--outline" href="#signup">
                Foster or volunteer
              </a>
            </div>

            <div className="cta__form" id="signup">
              <SubscribeForm />
            </div>
          </div>
        </section>
      </main>

      {/* ---------- Footer ---------- */}
      <footer className="site-footer">
        <div className="wrap site-footer__inner">
          <img className="site-footer__mark" src="/ticomi-logo.webp" alt="" width="76" height="76" />
          <span className="site-footer__name">TiCoMi Foundation</span>
          <span className="site-footer__status">501(c)(3) Nonprofit Organization</span>
          <span className="site-footer__tag">Different Hearts, One Love</span>
        </div>
      </footer>
    </>
  );
}
